// Central config + controlled vocabularies for Booth Tracker v2.
// Values that will become real in later slices (MCP endpoint, Entra IDs) are
// read from Vite env so nothing secret is committed. Slice 1 needs none of them
// at runtime — sign-in is stubbed — but they're declared here so the auth slice
// is a drop-in.

import { version } from '../package.json';

/** App version, sourced from package.json so the header badge bumps automatically. */
export const APP_VERSION = version;

/**
 * Default `show` values (the seed set). v0.5 widens `Show` to `string` so custom
 * shows can be added at runtime via the controlled "+ Add show…" action (see
 * src/config/shows.ts); this array is just the built-in seed for that union. The
 * codes are stable — do not rename an existing one (it would split de-dup keys).
 */
export const SHOWS = ['Evolve', 'WWIN', 'MAGIC', 'OffPrice', 'ASD', 'Closeout'] as const;
export type Show = string;

/** Booth priority (brief §"Data model"). */
export const PRIORITIES = ['revisit', 'order', 'pass'] as const;
export type Priority = (typeof PRIORITIES)[number];

/** Photo kinds attachable to a booth. */
export const PHOTO_KINDS = ['booth', 'product', 'notes'] as const;
export type PhotoKind = (typeof PHOTO_KINDS)[number];

/** On-device photo compression budget (reliability: keep IndexedDB small, sync cheap). */
export const PHOTO_COMPRESSION = {
  maxSizeMB: 0.6,
  maxWidthOrHeight: 1600,
  useWebWorker: true,
};

/**
 * Entra / MCP wiring. The SPA app registration now exists, so `entraClientId`
 * defaults to it and `authConfigured()` is true by default → src/auth/auth.ts
 * lazy-loads MSAL.js for real sign-in. Every value is env-overridable (Vite
 * inlines `VITE_*` at build time); the client id is a public identifier, not a
 * secret, so defaulting it here is safe and keeps the deployed build working
 * without extra SWA build config.
 */
export const AUTH = {
  // SPA / public-client app registration for the PWA (NOT the MCP resource app).
  entraClientId: import.meta.env.VITE_ENTRA_CLIENT_ID ?? '0554ccee-2354-4958-9a50-d3bb4bbd6733',
  tenantId: import.meta.env.VITE_ENTRA_TENANT_ID ?? '0196d5c8-9ee7-4581-998a-cefe3171a4d7',
  // Delegated scope against the MCP resource app (db25a511-…). api://<MCP_APP_ID>/access_as_user.
  mcpScope: import.meta.env.VITE_MCP_SCOPE ?? 'api://db25a511-79d2-4b74-9793-4186da141981/access_as_user',
  mcpEndpoint: import.meta.env.VITE_MCP_ENDPOINT ?? 'https://sss-mcp-prod-app.azurewebsites.net/mcp',
  // MSAL redirect target. Empty → auth.ts derives `${origin}${BASE_URL}` so the
  // login popup lands on the app's own page (under /booth-tracker/), not the bare
  // host root. Override per-env (e.g. a custom domain) with VITE_REDIRECT_URI.
  // Whatever resolves here MUST be a registered SPA redirect URI on the app reg.
  redirectUri: import.meta.env.VITE_REDIRECT_URI ?? '',
};

/** True when a real Entra SPA client id is configured (drives the MSAL path). */
export const authConfigured = (): boolean => AUTH.entraClientId.trim().length > 0;

/**
 * Booth-tracker backend (Azure Functions). Same-origin `/api` on the deployed
 * Static Web App (no CORS). For local dev against a separate Functions host, set
 * VITE_API_BASE (e.g. http://localhost:7071/api).
 */
export const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';
